"use client"

import { useState, useRef, useEffect } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "../../hooks/useAuth"

const Navbar = () => {
  const { user, logout } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef()
  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = () => {
    logout()
    navigate("/")
  }

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [])

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const isActive = (path) => {
    return location.pathname === path ? "bg-amber-700 text-white" : "text-white hover:bg-amber-700"
  }

  const customerLinks = (
    <>
      <Link
        to="/browse-restaurants"
        className={`${isActive("/browse-restaurants")} px-3 py-2 rounded-md transition-colors duration-200`}
      >
        Restaurants
      </Link>
      <Link
        to="/order-tracking"
        className={`${isActive("/order-tracking")} px-3 py-2 rounded-md transition-colors duration-200`}
      >
        My Orders
      </Link>
      <Link to="/payment" className={`${isActive("/payment")} px-3 py-2 rounded-md transition-colors duration-200`}>
        Become Owner
      </Link>
    </>
  )

  const ownerLinks = (
    <>
      <Link
        to="/owner/dashboard"
        className={`${isActive("/owner/dashboard")} px-3 py-2 rounded-md transition-colors duration-200`}
      >
        Dashboard
      </Link>
      <Link
        to="/owner/restaurant-management"
        className={`${isActive("/owner/restaurant-management")} px-3 py-2 rounded-md transition-colors duration-200`}
      >
        Manage Restaurant
      </Link>
      <Link
        to="/owner/menu-management"
        className={`${isActive("/owner/menu-management")} px-3 py-2 rounded-md transition-colors duration-200`}
      >
        Manage Menu
      </Link>
      <Link
        to="/owner/order-management"
        className={`${isActive("/owner/order-management")} px-3 py-2 rounded-md transition-colors duration-200`}
      >
        Manage Orders
      </Link>
      <Link
        to="/owner/chats"
        className={`${isActive("/owner/chats")} px-3 py-2 rounded-md transition-colors duration-200`}
      >
        Chats
      </Link>
      <Link
        to="/owner/analytics"
        className={`${isActive("/owner/analytics")} px-3 py-2 rounded-md transition-colors duration-200`}
      >
        Analytics
      </Link>
    </>
  )

  return (
    <nav className="bg-amber-600 shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <svg
                className="h-8 w-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                ></path>
              </svg>
              <span className="ml-2 text-white font-bold text-xl">RestaurantApp</span>
            </Link>
            <div className="hidden md:flex items-center space-x-1 ml-10">
              <Link to="/" className={`${isActive("/")} px-3 py-2 rounded-md transition-colors duration-200`}>
                Home
              </Link>
              {!user && (
                <>
                  <Link
                    to="/login"
                    className={`${isActive("/login")} px-3 py-2 rounded-md transition-colors duration-200`}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className={`${isActive("/register")} px-3 py-2 rounded-md transition-colors duration-200`}
                  >
                    Register
                  </Link>
                </>
              )}
              {user && user.role === "customer" && customerLinks}
              {user && user.role === "owner" && ownerLinks}
            </div>
          </div>

          {user && (
            <div className="hidden md:flex items-center ml-auto relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((o) => !o)}
                className="flex items-center text-white hover:bg-amber-700 px-3 py-2 rounded-md focus:outline-none transition-colors duration-200"
              >
                <div className="w-8 h-8 rounded-full bg-amber-800 flex items-center justify-center text-white mr-2">
                  {(user.username || user.email).charAt(0).toUpperCase()}
                </div>
                <span>{user.username || user.email}</span>
                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 mt-12 w-48 bg-white rounded-md shadow-lg z-20 py-1 border border-gray-200 animate-fadeIn">
                  <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                    Signed in as <span className="font-medium text-gray-900">{user.email}</span>
                  </div>
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        ></path>
                      </svg>
                      Profile
                    </div>
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        ></path>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        ></path>
                      </svg>
                      Settings
                    </div>
                  </Link>
                  <hr className="my-1 border-gray-200" />
                  <button
                    onClick={handleLogout}
                    className="w-full text-left block px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <div className="flex items-center text-red-600">
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        ></path>
                      </svg>
                      Logout
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="md:hidden ml-auto">
            <button
              onClick={() => setMobileOpen((o) => !o)}
              className="text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white p-1"
              aria-expanded={mobileOpen}
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-amber-700 rounded-b-lg shadow-lg animate-fadeIn">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                className={`${isActive("/")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
              >
                Home
              </Link>
              {!user && (
                <>
                  <Link
                    to="/login"
                    className={`${isActive("/login")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className={`${isActive("/register")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Register
                  </Link>
                </>
              )}
              {user && user.role === "customer" && (
                <>
                  <Link
                    to="/browse-restaurants"
                    className={`${isActive("/browse-restaurants")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Restaurants
                  </Link>
                  <Link
                    to="/order-tracking"
                    className={`${isActive("/order-tracking")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    My Orders
                  </Link>
                  <Link
                    to="/payment"
                    className={`${isActive("/payment")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Become Owner
                  </Link>
                </>
              )}
              {user && user.role === "owner" && (
                <>
                  <Link
                    to="/owner/dashboard"
                    className={`${isActive("/owner/dashboard")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/owner/restaurant-management"
                    className={`${isActive("/owner/restaurant-management")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Manage Restaurant
                  </Link>
                  <Link
                    to="/owner/menu-management"
                    className={`${isActive("/owner/menu-management")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Manage Menu
                  </Link>
                  <Link
                    to="/owner/order-management"
                    className={`${isActive("/owner/order-management")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Manage Orders
                  </Link>
                  <Link
                    to="/owner/chats"
                    className={`${isActive("/owner/chats")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Chats
                  </Link>
                  <Link
                    to="/owner/analytics"
                    className={`${isActive("/owner/analytics")} block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200`}
                  >
                    Analytics
                  </Link>
                </>
              )}
              {user && (
                <>
                  <hr className="border-amber-600 my-2" />
                  <Link
                    to="/profile"
                    className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-amber-800 transition-colors duration-200"
                  >
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white hover:bg-amber-800 transition-colors duration-200"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

export default Navbar
